/**
 * Home Page - Landing page for Bitcoin.com Loans & Mortgages
 */

import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { SEO, websiteSchema, organizationSchema, financialProductSchema } from '../components/common/SEO'
import { Button } from '../components/common/Button'
import { Card, CardContent } from '../components/common/Card'
import { VaultIcon, DocumentIcon, CurrencyIcon, LockIcon } from '../assets/icons'
import { FeaturesSection } from '@bitcoin-com/react-components/src/components/features-section'
import { 
  BlockSecureIllustration,
  CoinsLockIllustration,
  WalletConnectIllustration,
  SuccessCoinIllustration,
  BitcoinWalletNoteIllustration,
  CoinBooksIllustration
} from '@bitcoin-com/react-components/src/components/mini-illustrations'

// Full Illustrations for hero and feature sections - using public folder for reliable Cloudflare builds
const IllustrationAccount = '/illustrations/Illustration-Account.svg'
const IllustrationWallet = '/illustrations/Illustration-Wallet.svg'
const IllustrationBitcoinCheckmark = '/illustrations/Illustration-Bitcoin-Checkmark.svg'

export function HomePage() {
  // BUILD VERIFICATION: 2025-12-22 15:35 UTC - Testing Cloudflare deployment
  // If you see this comment, the build is from the latest commit
  const { isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()

  const features = [
    {
      title: 'Crypto Vaults',
      description: 'Deposit BTC, ETH, and other assets as collateral in secure smart contract vaults.',
      Icon: VaultIcon,
      illustration: BlockSecureIllustration
    },
    {
      title: 'Letters of Credit',
      description: 'Issue trustless LOCs backed by your collateral for payments and guarantees.',
      Icon: DocumentIcon,
      illustration: BitcoinWalletNoteIllustration
    },
    {
      title: 'Fiat Off-Ramps',
      description: 'Access EUR, USD, and other fiat currencies through partner bank integrations.',
      Icon: CurrencyIcon,
      illustration: CoinBooksIllustration
    },
    {
      title: 'Overcollateralized',
      description: 'Maintain healthy collateral ratios with real-time price feeds and liquidation protection.',
      Icon: LockIcon,
      illustration: CoinsLockIllustration
    },
  ]

  // Use cases for crypto-backed loans
  const useCases = [
    {
      title: 'Car Loans & Major Purchases',
      description: 'Buy your dream car without selling your Bitcoin. Keep your crypto investment growing while getting instant liquidity for vehicle purchases.',
      illustration: CoinsLockIllustration,
      badge: 'Instant Approval'
    },
    {
      title: 'Short-Term Cash Advances',
      description: 'Cover unexpected expenses or bridge cash flow gaps. Access funds in minutes without credit checks or lengthy applications.',
      illustration: WalletConnectIllustration,
      badge: 'No Credit Check'
    },
    {
      title: 'Real Estate & Mortgages',
      description: 'Secure property down payments or full mortgages using your crypto portfolio. Borrow large amounts while maintaining your position.',
      illustration: BlockSecureIllustration,
      badge: 'Large Amounts'
    },
    {
      title: 'Tax-Smart Strategy',
      description: 'Borrowing is not a taxable event. Keep your crypto, avoid capital gains taxes, and maintain your long-term investment strategy.',
      illustration: SuccessCoinIllustration,
      badge: 'Tax Efficient'
    },
  ]

  // Features for FeaturesSection component with FULL SVG illustrations
  const mainFeatures = [
    {
      id: '1',
      title: 'Unlock Your Crypto Value',
      description: 'Use your Bitcoin and Ethereum as collateral to access instant loans without selling your assets. Maintain your crypto positions while getting the liquidity you need for real-world expenses.',
      buttonText: 'Get Started',
      onButtonClick: () => isConnected ? window.location.href = '/vaults' : openConnectModal?.(),
      imageElement: (
        <div className="w-full h-full flex items-center justify-center p-m">
          <div className="w-[400px] h-[400px] flex items-center justify-center">
            <img src={IllustrationWallet} alt="Unlock Crypto Value" className="w-full h-full object-contain" />
          </div>
        </div>
      ),
      imagePosition: 'left' as const
    },
    {
      id: '2',
      title: 'Secure & Transparent',
      description: 'Built on audited smart contracts with real-time price feeds from Chainlink oracles. Your collateral is held in non-custodial vaults, giving you complete control and transparency over your assets.',
      buttonText: 'Learn More',
      onButtonClick: () => window.location.href = 'https://docs.anvilapplication.com',
      imageElement: (
        <div className="w-full h-full flex items-center justify-center p-m">
          <div className="w-[400px] h-[400px] flex items-center justify-center">
            <img src={IllustrationBitcoinCheckmark} alt="Secure and Audited" className="w-full h-full object-contain" />
          </div>
        </div>
      ),
      imagePosition: 'right' as const
    }
  ]

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      websiteSchema,
      organizationSchema,
      financialProductSchema,
    ],
  }

  return (
    <>
      <SEO
        title="Borrow Against Your Crypto"
        description="Keep your Bitcoin, access cash for cars, homes, and more. Secure crypto-backed loans and mortgages with competitive rates. Borrow don't sell and save more on taxes."
        canonical="/"
        jsonLd={homeJsonLd}
      />
      
      {/* Hero Section - With Full SVG Illustration */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] lg:py-[100px] bg-shades-white">
        <div className="max-w-[1240px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-xl items-center">
            {/* Left: Content */}
            <div className="flex flex-col gap-l">
              {/* Main Heading - No colored text */}
              <h1 className="font-display font-black text-[44px] md:text-[56px] lg:text-[80px] leading-snug uppercase text-text-primary">
                Bitcoin Loans and Mortgages
              </h1>
              
              {/* Subtitle */}
              <p className="text-body-lg md:text-heading-sm text-text-secondary leading-relaxed font-sans max-w-[600px]">
                Unlock the value of your Bitcoin and crypto with secure, overcollateralized loans without giving up custody of your funds.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-start gap-s">
                {isConnected ? (
                  <Link to="/dashboard">
                    <Button variant="primary" size="lg">Go to Dashboard</Button>
                  </Link>
                ) : (
                  <Button variant="primary" size="lg" onClick={openConnectModal}>
                    Connect Wallet to Start
                  </Button>
                )}
                <Link to="/about">
                  <Button variant="default" size="lg">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Full Illustration - Account */}
            <div className="flex items-center justify-center lg:justify-end">
              <div className="w-full max-w-[500px] md:max-w-[600px] lg:max-w-[700px] flex items-center justify-center">
                <img src={IllustrationAccount} alt="Bitcoin Loans Account" className="w-full h-auto" style={{ minHeight: '400px' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - Why Choose Bitcoin.com Loans */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[1240px] mx-auto">
          {/* Section Heading */}
          <div className="text-center mb-xl">
            <h2 className="font-display font-black text-heading-xl md:text-[56px] uppercase text-text-primary mb-s leading-compact">
              Use Your Crypto for Life's Big Moments
            </h2>
            <p className="text-body-lg text-text-secondary max-w-[700px] mx-auto leading-relaxed">
              Don't sell your crypto. Borrow against it and save on taxes while keeping your investment growing.
            </p>
          </div>

          {/* Use Case Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-s md:gap-m">
            {useCases.map((useCase) => {
              const Illustration = useCase.illustration
              return (
                <Card key={useCase.title} interactive padding="lg">
                  <CardContent className="text-center relative">
                    {/* Badge */}
                    <div className="absolute top-0 right-0 px-s py-xs rounded-pill bg-secondary-10">
                      <span className="text-secondary-100 font-semiBold font-sans text-label-sm">{useCase.badge}</span>
                    </div>
                    
                    {/* Mini Illustration */}
                    <div className="flex justify-center mb-m mt-xs">
                      <div className="w-[80px] h-[80px]">
                        <Illustration className="w-full h-full" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-sans font-semiBold text-body-lg mb-s text-text-primary">
                      {useCase.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-body-sm text-text-secondary leading-relaxed">
                      {useCase.description}
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Tax Benefit Callout */}
          <div className="mt-xl bg-secondary-10 rounded-xl p-xl text-center border border-secondary-20">
            <div className="inline-flex items-center gap-s mb-s">
              <div className="w-[48px] h-[48px]">
                <SuccessCoinIllustration className="w-full h-full" />
              </div>
              <h3 className="font-display font-black text-heading-sm uppercase text-secondary-100">
                Borrow, Don't Sell
              </h3>
            </div>
            <p className="text-body-md text-text-secondary max-w-[800px] mx-auto leading-relaxed">
              When you borrow against your crypto, you avoid triggering capital gains taxes. Keep your Bitcoin working for you while accessing the cash you need. It's the smart way to use your crypto portfolio.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - Two Features with Full SVG Illustrations */}
      <FeaturesSection
        style="light"
        heading="Why Choose Bitcoin.com Loans"
        features={mainFeatures}
      />

      {/* How It Works Section - Mini Illustrations ONLY for replacing icon emojis */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] bg-shades-extra-light">
        <div className="max-w-[1240px] mx-auto">
          {/* Section Heading */}
          <div className="text-center mb-xl">
            <h2 className="font-display font-black text-heading-xl md:text-[56px] uppercase text-text-primary mb-s leading-compact">
              How It Works
            </h2>
            <p className="text-body-lg text-text-secondary max-w-[600px] mx-auto leading-relaxed">
              Four simple steps to unlock the value of your crypto assets
            </p>
          </div>
          
          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-s md:gap-m">
            {features.map((feature, index) => {
              const Illustration = feature.illustration
              return (
                <Card key={feature.title} interactive padding="lg">
                  <CardContent className="text-center relative">
                    {/* Step Number Badge */}
                    <div className="absolute top-0 right-0 w-[32px] h-[32px] rounded-pill bg-secondary-10 flex items-center justify-center">
                      <span className="text-secondary-100 font-bold font-mono text-label-sm">{index + 1}</span>
                    </div>
                    
                    {/* Mini Illustration - replacing icon/emoji */}
                    <div className="flex justify-center mb-m">
                      <div className="w-[64px] h-[64px]">
                        <Illustration className="w-full h-full" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-sans font-semiBold text-body-lg mb-s text-text-primary">
                      {feature.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-body-sm text-text-secondary leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Highlight CTA Section with Footer Element - GRAY BACKGROUND */}
      <section className="px-m md:px-xl bg-shades-extra-light">
        <div className="max-w-[1240px] mx-auto">
          <div className="relative bg-shades-white rounded-xl overflow-hidden border border-border shadow-md">
            {/* Main Content */}
            <div className="relative z-10 p-xl md:p-xxl text-center">
              {/* Large Full SVG Illustration */}
              <div className="flex justify-center mb-l">
                <div className="w-[200px] h-[200px] md:w-[280px] md:h-[280px]">
                  <SuccessCoinIllustration className="w-full h-full" />
                </div>
              </div>
              
              {/* Heading */}
              <h2 className="font-display font-black text-heading-xl md:text-[56px] uppercase text-text-primary mb-m leading-compact">
                Ready to get started?
              </h2>
              
              {/* Description */}
              <p className="text-body-lg md:text-heading-sm text-text-secondary mb-xl max-w-[700px] mx-auto leading-relaxed">
                Connect your wallet and create your first vault in minutes. No credit checks, instant approval.
              </p>
              
              {/* CTA Button */}
              {!isConnected ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={openConnectModal}
                >
                  Connect Wallet Now
                </Button>
              ) : (
                <Link to="/vaults">
                  <Button variant="primary" size="lg">
                    Create Your First Vault
                  </Button>
                </Link>
              )}
            </div>
            
            {/* Footer Element - Trust Badges */}
            <div className="relative z-10 bg-shades-extra-light border-t border-border px-xl py-l">
              <div className="flex flex-col md:flex-row items-center justify-center gap-m md:gap-xl text-text-primary">
                <div className="flex items-center gap-s">
                  <div className="w-[48px] h-[48px]">
                    <BlockSecureIllustration className="w-full h-full" />
                  </div>
                  <span className="text-label-lg font-semiBold">Audited Smart Contracts</span>
                </div>
                <div className="hidden md:block w-[1px] h-[24px] bg-border"></div>
                <div className="flex items-center gap-s">
                  <div className="w-[48px] h-[48px]">
                    <CoinsLockIllustration className="w-full h-full" />
                  </div>
                  <span className="text-label-lg font-semiBold">Non-Custodial Vaults</span>
                </div>
                <div className="hidden md:block w-[1px] h-[24px] bg-border"></div>
                <div className="flex items-center gap-s">
                  <div className="w-[48px] h-[48px]">
                    <WalletConnectIllustration className="w-full h-full" />
                  </div>
                  <span className="text-label-lg font-semiBold">Bitcoin Loans & Mortgages</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Using Mini Illustration for icon replacement */}
      <section className="px-m md:px-xl py-[60px] md:py-[80px] pb-0 bg-shades-extra-light">
        <div className="max-w-[1240px] mx-auto text-center bg-surface rounded-xl p-xl md:p-xxl border border-border">
          {/* Mini Illustration - replacing icon/emoji */}
          <div className="flex justify-center mb-m">
            <div className="w-[96px] h-[96px]">
              <BlockSecureIllustration className="w-full h-full" />
            </div>
          </div>
          
          {/* Heading */}
          <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-s leading-compact">
            Audited & Secure
          </h2>
          
          {/* Description */}
          <p className="text-body-lg text-text-secondary mb-xl max-w-[700px] mx-auto leading-relaxed">
            Smart contracts audited by industry leaders. Built on battle-tested Anvil Protocol with zero exploits.
          </p>
          
          {/* Partner Badges */}
          <div className="flex flex-wrap justify-center items-center gap-m">
            {['OpenZeppelin', 'Trail of Bits', 'Anvil Protocol', 'Amdax'].map((partner) => (
              <div key={partner} className="px-m py-s bg-surface-muted rounded-sm border border-border">
                <span className="text-label font-semiBold text-text-primary">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
